import { SubscriptionPlan, Subscription } from '../stores/subscriptionStore'
import { config } from '../config/environment'

declare global {
  interface Window {
    Razorpay: any
  }
}

export interface PaymentResponse {
  success: boolean
  paymentId?: string
  subscription?: Subscription
  error?: string
}

export interface CreateOrderResponse {
  success: boolean
  orderId?: string
  error?: string
}

// Helper function to get auth token
const getAuthToken = (): string | null => {
  // First try to get JWT token, fallback to authToken (uid) for development
  const jwtToken = localStorage.getItem('jwtToken');
  if (jwtToken) {
    return jwtToken;
  }
  
  // For development mode, use the uid as a simple identifier
  const uid = localStorage.getItem('authToken');
  if (uid) {
    return `dev_${uid}`;
  }
  
  return null;
};

class PaymentService {
  private loadRazorpayScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (window.Razorpay) {
        resolve()
        return
      }

      const script = document.createElement('script')
      script.src = 'https://checkout.razorpay.com/v1/checkout.js'
      script.onload = () => resolve()
      script.onerror = () => reject(new Error('Failed to load Razorpay'))
      document.head.appendChild(script)
    })
  }

  async createOrder(plan: SubscriptionPlan, userEmail: string, userName: string): Promise<CreateOrderResponse> {
    try {
      // Get the actual price based on user location
      const actualPrice = (plan as any).price || plan.price
      const actualCurrency = (plan as any).currency || plan.currency
      
      console.log('Creating order for plan:', plan.id, 'amount:', actualPrice, 'currency:', actualCurrency, 'user:', userEmail)
      
      const requestBody = {
        planId: plan.id,
        amount: actualPrice,
        currency: actualCurrency,
        userEmail,
        userName
      }
      
      console.log('Request body:', requestBody)
      
      const response = await fetch(`${config.apiBaseUrl}/api/payments/create-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await getAuthToken()}`
        },
        body: JSON.stringify(requestBody)
      })

      console.log('Response status:', response.status)
      
      if (!response.ok) {
        const errorText = await response.text()
        console.error('HTTP error:', response.status, errorText)
        return {
          success: false,
          error: `HTTP ${response.status}: ${errorText}`
        }
      }

      const data = await response.json()
      console.log('Order response:', data)
      return data
    } catch (error) {
      console.error('Error creating order:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create payment order'
      }
    }
  }

  async verifyPayment(paymentId: string, orderId: string, signature: string, plan: SubscriptionPlan, userEmail: string, userName: string): Promise<PaymentResponse> {
    try {
      // Get the actual price based on user location
      const actualPrice = (plan as any).price || plan.price
      
      const response = await fetch(`${config.apiBaseUrl}/api/payments/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await getAuthToken()}`
        },
        body: JSON.stringify({
          paymentId,
          orderId,
          signature,
          planId: plan.id,
          userEmail,
          userName,
          amount: actualPrice
        })
      })

      const data = await response.json()
      return data
    } catch (error) {
      console.error('Error verifying payment:', error)
      return {
        success: false,
        error: 'Failed to verify payment'
      }
    }
  }

  async initiatePayment(plan: SubscriptionPlan, userEmail: string, userName: string): Promise<PaymentResponse> {
    try {
      console.log('Initiating payment for plan:', plan.id, 'user:', userEmail)
      
      // Check if Razorpay key is configured
      if (!config.razorpayKeyId) {
        console.error('Razorpay key not configured')
        return {
          success: false,
          error: 'Payment gateway not configured. Please contact support.'
        }
      }
      
      // Load Razorpay script
      await this.loadRazorpayScript()
      console.log('Razorpay script loaded successfully')

      // Create order
      const orderResponse = await this.createOrder(plan, userEmail, userName)
      console.log('Order creation response:', orderResponse)
      
      if (!orderResponse.success || !orderResponse.orderId) {
        console.error('Order creation failed:', orderResponse.error)
        return {
          success: false,
          error: orderResponse.error || 'Failed to create order'
        }
      }

      // Configure Razorpay options
      const options = {
        key: config.razorpayKeyId, // Use environment variable
        amount: plan.price * 100, // Convert to paise for Razorpay
        currency: plan.currency,
        name: 'Rraasi - Cosmic AI',
        description: `${plan.name} Subscription - ${plan.rraasiCoins} RRAASI Coins`,
        order_id: orderResponse.orderId,
        prefill: {
          name: userName,
          email: userEmail
        },
        theme: {
          color: '#8B5CF6'
        },
        handler: async (response: any) => {
          console.log('Payment successful, verifying...', response)
          // Verify payment
          const verificationResponse = await this.verifyPayment(
            response.razorpay_payment_id,
            response.razorpay_order_id,
            response.razorpay_signature,
            plan,
            userEmail,
            userName
          )

          if (verificationResponse.success && verificationResponse.subscription) {
            // Update subscription in store
            const { useSubscriptionStore } = await import('../stores/subscriptionStore')
            useSubscriptionStore.getState().setCurrentSubscription(verificationResponse.subscription)
            
            // Show success message with RRAASI coin information
            const { toast } = await import('react-hot-toast')
            toast.success(
              `Payment successful! 🎉\n${plan.rraasiCoins} RRAASI Coins added to your account!`,
              { duration: 5000 }
            )
            
            // Show additional coin info
            setTimeout(() => {
              toast.success(
                `Your ${plan.name} subscription is now active!\nUse your RRAASI coins to unlock premium features.`,
                { duration: 4000 }
              )
            }, 1000)
          } else {
            const { toast } = await import('react-hot-toast')
            toast.error(verificationResponse.error || 'Payment verification failed')
          }
        },
        modal: {
          ondismiss: () => {
            console.log('Payment modal dismissed')
          }
        }
      }

      console.log('Opening Razorpay modal with options:', options)
      
      // Open Razorpay modal
      const razorpay = new window.Razorpay(options)
      razorpay.open()

      return {
        success: true
      }
    } catch (error) {
      console.error('Error initiating payment:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to initiate payment'
      }
    }
  }

  async getSubscriptionStatus(): Promise<Subscription | null> {
    try {
      const user = JSON.parse(localStorage.getItem('user') || 'null')
      const response = await fetch(`${config.apiBaseUrl}/api/payments/subscription-status`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-user-email': user?.email || '',
          'Authorization': `Bearer ${await getAuthToken()}`
        }
      })

      const data = await response.json()
      return data.success ? data.subscription : null
    } catch (error) {
      console.error('Error fetching subscription status:', error)
      return null
    }
  }

  async cancelSubscription(): Promise<boolean> {
    try {
      const user = JSON.parse(localStorage.getItem('user') || 'null')
      const response = await fetch(`${config.apiBaseUrl}/api/payments/cancel-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-email': user?.email || '',
          'Authorization': `Bearer ${await getAuthToken()}`
        }
      })

      const data = await response.json()
      return data.success
    } catch (error) {
      console.error('Error cancelling subscription:', error)
      return false
    }
  }
}

export const paymentService = new PaymentService() 